import {RefObject} from "react";
import {Device} from "../../types/device";
import SpecificationsBlock from "./SpecificationsBlock";

type Props = {
    device?: Device;
    showAllCategories: boolean;
    specsRef: RefObject<HTMLDivElement | null>;
    onToggleShowAll: () => void;
};

export default function PhoneSpecifications({device, showAllCategories, specsRef, onToggleShowAll}: Props) {
    return (
        <div className="col-span-3 flex flex-col gap-8 scroll-mt-16" id="specs" ref={specsRef}>

            <SpecificationsBlock
                title="Screen"
                items={[
                    {
                        label: "Screen Size",
                        value: device?.screen_size_inch ? `${device.screen_size_inch} ''` : null,
                    },
                    {label: "Resolution", value: device?.screen_resolution},
                    {label: "Pixel Density", value: device?.ppi ? `${device.ppi} ppi` : null},
                    {label: "Panel Type", value: device?.screen_type},
                    {label: "Refresh Rate", value: device?.refresh_rate_hz ? `${device.refresh_rate_hz} hz` : null},
                    {label: "HDR Support", value: device?.hdr_support ? "Yes" : "No"},
                    {label: "Protection", value: device?.glass_type},
                    {label: "Other Display Specs", value: device?.screen_other},
                ]}
            />

            <SpecificationsBlock
                title="Performance"
                items={[
                    {label: "CPU Model", value: device?.cpu_model},
                    {
                        label: "CPU Speed",
                        value: device?.cpu_frequency_ghz ? `${device.cpu_frequency_ghz} ghz` : null,
                    },
                    {label: "CPU Cores", value: device?.cpu_cores},
                    {label: "GPU (Graphics)", value: device?.gpu_model},
                    {label: "RAM (Memory)", value: device?.ram_gb ? `${device.ram_gb} GB` : null},
                    {
                        label: "Internal Storage",
                        value: device?.storage_gb?.map((val, i) =>
                            val + (i === (device?.storage_gb?.length ?? 0) - 1 ? " GB" : "GB, ")
                        ),
                    },
                    {label: "Memory Card Slot", value: device?.memory_slot},
                ]}
            />
            <div
                className={`flex flex-col gap-8 overflow-hidden transition-all duration-700 ease-in-out ${
                    showAllCategories ? "max-h-[5000px] opacity-100" : "max-h-0 opacity-0"
                }`}
            >
                <SpecificationsBlock
                    title="Main Camera (Rear)"
                    items={[
                        {
                            label: "Main Sensor",
                            value: device?.main_camera_mp ? `${device.main_camera_mp} mp` : null,
                        },
                        {label: "Aperture", value: device?.main_camera_aperture},
                        {
                            label: "Focal Length",
                            value: device?.main_camera_focal_length_mm ? `${device.main_camera_focal_length_mm} mm` : null,
                        },
                        {
                            label: "Ultrawide Lens",
                            value: device?.ultrawide_camera_mp ? `${device.ultrawide_camera_mp} mp` : null,
                        },
                        {
                            label: "Telephoto Lens",
                            value: device?.telephoto_camera_mp ? `${device.telephoto_camera_mp} mp` : null,
                        },
                        {label: "Optical Zoom", value: device?.camera_zoom_optical},
                        {label: "Digital Zoom", value: device?.camera_zoom_digital},
                        {label: "Video Recording", value: device?.video_resolution},
                        {label: "Total Cameras", value: device?.number_of_cameras},
                    ]}
                />

                <SpecificationsBlock
                    title="Front Camera (Selfie)"
                    items={[
                        {
                            label: "Front Resolution",
                            value: device?.front_camera_mp ? `${device.front_camera_mp} mp` : null,
                        },
                        {label: "Front Aperture", value: device?.front_camera_aperture},
                        {label: "Field of View (FOV)", value: device?.front_camera_fov_deg},
                    ]}
                />

                <SpecificationsBlock
                    title="Connectivity & Sensors"
                    items={[
                        {
                            label: "Wi-Fi & Bluetooth",
                            value: `${device?.wifi_version || ""}${device?.wifi_version && device?.bluetooth_version ? ", " : ""}${device?.bluetooth_version || ""}`,
                        },
                        {label: "NFC", value: device?.nfc ? "Yes" : "No"},
                        {label: "GPS", value: device?.gps ? "Yes" : "No"},
                        {label: "Charging Port", value: device?.charging_port},
                        {
                            label: "Biometrics",
                            value: [device?.face_scanner && "Face scanner", device?.fingerprint_scanner && "Fingerprint scanner"]
                                .filter(Boolean)
                                .join(", "),
                        },
                        {label: "Durability", value: device?.waterproof},
                        {label: "Extra Features", value: device?.extra_features?.toString()},
                    ]}
                />

                <SpecificationsBlock
                    title="Battery"
                    items={[
                        {
                            label: "Capacity",
                            value: device?.battery_capacity_mah ? `${device.battery_capacity_mah} mah` : null,
                        },
                        {
                            label: "Charging Power",
                            value: device?.charger_power_w ? `${device.charger_power_w} w` : null,
                        },
                        {label: "Wireless Charging", value: device?.wireless_charging ? "Yes" : "No"},
                    ]}
                />

                <SpecificationsBlock
                    title="General Info"
                    items={[
                        {label: "Brand", value: device?.brand},
                        {label: "Model", value: device?.model},
                        {label: "Release Date", value: device?.release_date},
                        {label: "Weight", value: device?.weight_g ? `${device.weight_g} g` : null},
                        {
                            label: "Dimensions",
                            value: device?.length ? `${device.length} mm, ${device.width} mm, ${device.thickness} mm` : null,
                        },
                        {label: "Operating System", value: device?.os},
                        {label: "Network Support", value: device?.network},
                        {
                            label: "SIM Slots / eSIM",
                            value: `${device?.sim_slots} / ${device?.e_sim ? "Yes" : "No"}`,
                        },
                    ]}
                />
            </div>
            <div className="flex justify-center gap-10 allign-items-center">
                <button
                    className="bg-[#1B4498] text-white px-10 py-3 rounded-full"
                    onClick={() => {
                        if (showAllCategories) {
                            specsRef.current?.scrollIntoView({behavior: "smooth", block: "start"});
                            setTimeout(onToggleShowAll, 200);
                        } else {
                            onToggleShowAll();
                        }
                    }}
                >
                    {showAllCategories ? "Less" : "More"}
                </button>
            </div>
        </div>
    );
}


